import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';

export interface CategoryItem {
    id: string | number;
    name: string;
    count: number;
    slug: string;
    children?: CategoryItem[];
    expanded?: boolean;
}

export interface BestSellerProduct {
    id: string | number;
    title: string;
    price: number;
    imageUrl: string;
    slug: string;
}

export interface PriceFilterEvent {
    minPrice: number;
    maxPrice: number;
}

@Component({
    selector: 'app-catalog-sidebar',
    standalone: true,
    imports: [CommonModule, FormsModule, RouterLink],
    template: `
    <aside class="w-full flex flex-col gap-8 bg-white p-4 rounded-3xl border border-gray-100 shadow-sm">
      
      <!-- 1. FILTRO DE PRECIO -->
      <div class="flex flex-col gap-4">
        <h3 class="font-bold text-gray-800 text-lg">Filtrar por precio</h3>
        
        <!-- Rango Slider Doble (Simulado) / Control de Rango -->
        <div class="relative pt-4 pb-2">
          <div class="h-1.5 bg-rose-200 rounded-full relative">
            <div 
              class="absolute h-full bg-rose-500 rounded-full" 
              [style.left.%]="(minPrice / absoluteMaxPrice) * 100"
              [style.right.%]="100 - ((maxPrice / absoluteMaxPrice) * 100)"
            ></div>
          </div>
          <!-- Sliders de Entrada -->
          <input 
            type="range" 
            [min]="0" 
            [max]="absoluteMaxPrice" 
            [(ngModel)]="minPrice"
            (ngModelChange)="onMinChange()"
            class="absolute top-2 w-full appearance-none bg-transparent pointer-events-auto cursor-pointer accent-rose-500"
          />
          <input 
            type="range" 
            [min]="0" 
            [max]="absoluteMaxPrice" 
            [(ngModel)]="maxPrice"
            (ngModelChange)="onMaxChange()"
            class="absolute top-2 w-full appearance-none bg-transparent pointer-events-auto cursor-pointer accent-rose-500"
          />
        </div>

        <!-- Inputs de Moneda (Desde / Hasta) -->
        <div class="grid grid-cols-2 gap-3">
          <div>
            <label class="text-xs text-gray-400 font-medium block mb-1">Desde</label>
            <div class="flex items-center bg-gray-50 rounded-2xl px-3 py-2 border border-gray-100">
              <span class="text-xs text-gray-400 font-bold mr-2">S/</span>
              <input 
                type="number" 
                [(ngModel)]="minPrice" 
                class="w-full bg-transparent font-bold text-gray-800 focus:outline-none text-sm"
              />
            </div>
          </div>

          <div>
            <label class="text-xs text-gray-400 font-medium block mb-1">Hasta</label>
            <div class="flex items-center bg-gray-50 rounded-2xl px-3 py-2 border border-gray-100">
              <span class="text-xs text-gray-400 font-bold mr-2">S/</span>
              <input 
                type="number" 
                [(ngModel)]="maxPrice" 
                class="w-full bg-transparent font-bold text-gray-800 focus:outline-none text-sm"
              />
            </div>
          </div>
        </div>

        <!-- ACCIONES (APLICAR / LIMPIAR) -->
        <div class="grid grid-cols-2 gap-3 mt-1">
          <button 
            (click)="applyFilter()"
            class="w-full py-3 bg-rose-500 hover:bg-rose-600 text-white font-bold text-xs uppercase rounded-2xl transition-all shadow-md shadow-rose-200 active:scale-95 cursor-pointer"
          >
            Aplicar Filtros
          </button>
          
          <button 
            (click)="resetFilter()"
            class="w-full py-3 bg-white hover:bg-gray-50 text-gray-500 font-semibold text-xs rounded-2xl border border-gray-200 transition-all cursor-pointer"
          >
            Limpiar
          </button>
        </div>
      </div>

      <hr class="border-gray-100" />

      <!-- 2. ARBOL DE CATEGORÍAS ACCESIBLE -->
      <div class="max-h-[380px] overflow-y-auto pr-1 flex flex-col gap-2 scrollbar-thin">
        <div *ngFor="let cat of categories" class="flex flex-col">
          
          <!-- Item Principal -->
          <div 
            class="group flex items-center justify-between p-3 rounded-2xl transition-all border"
            [ngClass]="cat.expanded ? 'bg-rose-50/50 border-rose-200 text-rose-600' : 'bg-gray-50/70 border-transparent hover:bg-gray-100 text-gray-700'"
          >
            <!-- Redirección por Clic -->
            <a 
              [routerLink]="['/catalogo', cat.slug]" 
              class="flex-1 font-semibold text-sm line-clamp-1 group-hover:text-rose-600"
            >
              {{ cat.name }}
            </a>

            <div class="flex items-center gap-2">
              <span class="text-xs font-bold px-2 py-0.5 rounded-full bg-gray-200/60 text-gray-500 group-hover:bg-rose-100 group-hover:text-rose-600">
                {{ cat.count }}
              </span>

              <!-- Botón Desplegable (si tiene hijos) -->
              <button 
                *ngIf="cat.children && cat.children.length > 0"
                (click)="toggleCategory(cat)"
                class="p-1 rounded-lg hover:bg-black/5 text-gray-400 cursor-pointer"
              >
                <svg 
                  class="w-4 h-4 transition-transform duration-200" 
                  [class.rotate-180]="cat.expanded"
                  fill="none" stroke="currentColor" viewBox="0 0 24 24"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>
          </div>

          <!-- Subcategorías (Acordeón) -->
          <div *ngIf="cat.expanded && cat.children" class="pl-4 mt-2 flex flex-col gap-2 border-l-2 border-rose-100 ml-3">
            <div 
              *ngFor="let sub of cat.children"
              class="flex items-center justify-between p-2.5 bg-white rounded-xl border border-gray-100 hover:border-rose-200 hover:text-rose-600 transition-colors"
            >
              <a [routerLink]="['/catalogo', sub.slug]" class="text-xs font-medium text-gray-600 hover:text-rose-600 flex items-center gap-1.5">
                <span class="text-gray-300">└</span> {{ sub.name }}
              </a>
              <span class="text-[11px] font-bold px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">
                {{ sub.count }}
              </span>
            </div>
          </div>

        </div>
      </div>

      <hr class="border-gray-100" />

      <!-- 3. PRODUCTOS MÁS VENDIDOS -->
      <div class="flex flex-col gap-4">
        <h3 class="font-bold text-gray-800 text-base">Productos más vendidos</h3>
        
        <div class="flex flex-col gap-3">
          <a 
            *ngFor="let item of bestSellers" 
            [routerLink]="['/producto', item.slug]"
            class="group flex items-center gap-3 p-1.5 rounded-2xl hover:bg-gray-50 transition-colors"
          >
            <img 
              [src]="item.imageUrl" 
              [alt]="item.title"
              class="w-14 h-14 object-cover rounded-full bg-rose-50 flex-shrink-0 group-hover:scale-105 transition-transform"
            />
            <div class="flex flex-col">
              <h4 class="text-xs font-bold text-gray-700 leading-snug group-hover:text-rose-600 transition-colors line-clamp-2">
                {{ item.title }}
              </h4>
              <span class="text-sm font-extrabold text-rose-500 mt-0.5">
                S/ {{ item.price.toFixed(2) }}
              </span>
            </div>
          </a>
        </div>
      </div>

      <!-- 4. BANNER PROMOCIONAL -->
      <a 
        [routerLink]="promoTargetUrl"
        class="relative block rounded-3xl overflow-hidden min-h-[320px] group cursor-pointer shadow-md"
      >
        <img 
          [src]="promoImageUrl" 
          alt="Promociones" 
          class="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
        />
        <div class="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
        <div class="absolute bottom-6 inset-x-0 flex flex-col items-center text-center px-4 z-10">
          <span class="text-white font-black text-xl tracking-wider uppercase mb-3 drop-shadow">
            PROMOCIONES
          </span>
          <span class="px-5 py-2 bg-white/20 backdrop-blur-md text-white font-bold text-xs uppercase rounded-full border border-white/40 group-hover:bg-white group-hover:text-gray-900 transition-all">
            Ver más
          </span>
        </div>
      </a>

    </aside>
  `,
    styles: [`
    input[type=range]::-webkit-slider-thumb {
      pointer-events: all;
      width: 18px;
      height: 18px;
      border-radius: 50%;
      border: 2px solid white;
      box-shadow: 0 1px 3px rgba(0,0,0,0.3);
    }
  `]
})
export class CatalogSidebarComponent {
    @Input() absoluteMaxPrice: number = 500;
    @Input() minPrice: number = 0;
    @Input() maxPrice: number = 493;

    @Input() categories: CategoryItem[] = [];
    @Input() bestSellers: BestSellerProduct[] = [];

    @Input() promoImageUrl: string = 'https://images.unsplash.com/photo-1518709268805-4e9042af9f23?q=80&w=800';
    @Input() promoTargetUrl: string = '/catalogo';

    @Output() filterApply = new EventEmitter<PriceFilterEvent>();

    onMinChange() {
        if (this.minPrice > this.maxPrice) {
            this.minPrice = this.maxPrice;
        }
    }

    onMaxChange() {
        if (this.maxPrice < this.minPrice) {
            this.maxPrice = this.minPrice;
        }
    }

    applyFilter() {
        this.filterApply.emit({
            minPrice: this.minPrice,
            maxPrice: this.maxPrice
        });
    }

    resetFilter() {
        this.minPrice = 0;
        this.maxPrice = this.absoluteMaxPrice;
        this.applyFilter();
    }

    toggleCategory(cat: CategoryItem) {
        cat.expanded = !cat.expanded;
    }
}